import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/app/Layout";
import { toast, Toaster } from "sonner";
import { Copy, Trash2, ExternalLink, Clock, Share2, Plus, CheckCircle2 } from "lucide-react";

export default function Shares() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedToken, setCopiedToken] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/share/list");
      setLinks(data);
    } catch {
      toast.error("Chargement impossible");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const revoke = async (token) => {
    if (!window.confirm("Révoquer ce lien ? Il deviendra immédiatement inaccessible.")) return;
    try {
      await api.delete(`/share/${token}`);
      toast.success("Lien révoqué");
      load();
    } catch { toast.error("Échec"); }
  };

  const copy = async (token) => {
    const url = `${window.location.origin}/s/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 2000);
      toast.success("Lien copié");
    } catch { toast.error("Copie impossible"); }
  };

  const isExpired = (exp) => {
    try { return new Date(exp) < new Date(); } catch { return false; }
  };

  return (
    <Layout>
      <Toaster richColors position="top-right" />
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <p className="overline mb-2">Partages</p>
          <h1 className="font-heading text-3xl sm:text-5xl font-black tracking-tighter leading-tight sm:leading-none">
            Liens actifs.
          </h1>
          <p className="text-gray-500 mt-3 max-w-lg">Gérez les liens publics que vous avez créés, copiez-les ou révoquez-les.</p>
        </div>
        <Link to="/dashboard" className="btn-secondary" data-testid="back-dashboard">
          <Plus size={14} /> Créer depuis le Dashboard
        </Link>
      </div>

      <div className="ra-card" data-testid="shares-table-card">
        {loading && <div className="progress-line" />}
        {(!loading && links.length === 0) ? (
          <div className="p-12 text-center">
            <div className="w-12 h-12 mx-auto mb-4 bg-gray-100 flex items-center justify-center">
              <Share2 className="text-gray-400" size={20} />
            </div>
            <p className="font-heading text-xl font-bold mb-1">Aucun lien de partage</p>
            <p className="text-sm text-gray-500 mb-4">Créez un lien depuis le Dashboard en cliquant sur « Partager ».</p>
            <Link to="/dashboard" className="btn-primary">Aller au Dashboard</Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="ra-table">
              <thead>
                <tr>
                  <th>Titre</th>
                  <th>Portée</th>
                  <th>Types</th>
                  <th>Créé</th>
                  <th>Expire</th>
                  <th style={{width: 200}}></th>
                </tr>
              </thead>
              <tbody>
                {links.map((l) => {
                  const expired = isExpired(l.expires_at);
                  return (
                    <tr key={l.token} data-testid={`share-row-${l.token}`}>
                      <td>
                        <div className="font-medium">{l.title || "Sans titre"}</div>
                        <div className="text-xs font-mono text-gray-400">{l.token.slice(0, 12)}...</div>
                      </td>
                      <td className="text-xs text-gray-600">
                        {l.month ? <span className="num">Mois {l.month}</span> : null}
                        {l.report_id ? <div>Rapport</div> : null}
                        {!l.month && !l.report_id ? "Tout" : null}
                      </td>
                      <td>
                        <div className="flex gap-1">
                          {(l.types || []).map((t) => <span key={t} className="badge">{t.toUpperCase()}</span>)}
                        </div>
                      </td>
                      <td className="num text-xs text-gray-500">{String(l.created_at).slice(0,10)}</td>
                      <td>
                        <span className={`num text-xs inline-flex items-center gap-1 ${expired ? "text-red-600 font-semibold" : "text-gray-600"}`}>
                          <Clock size={12} /> {String(l.expires_at).slice(0,10)}
                          {expired ? " (expiré)" : ""}
                        </span>
                      </td>
                      <td>
                        <div className="flex items-center gap-1 justify-end">
                          <a
                            href={`/s/${l.token}`}
                            target="_blank"
                            rel="noreferrer"
                            className="btn-ghost"
                            title="Ouvrir la page publique"
                            data-testid={`open-share-${l.token}`}
                          >
                            <ExternalLink size={14} />
                          </a>
                          <button className="btn-ghost" onClick={() => copy(l.token)} title="Copier le lien" data-testid={`copy-share-${l.token}`}>
                            {copiedToken === l.token ? <CheckCircle2 size={14} className="text-green-600" /> : <Copy size={14} />}
                          </button>
                          <button className="btn-ghost text-red-600 hover:bg-red-50" onClick={() => revoke(l.token)} title="Révoquer" data-testid={`revoke-share-${l.token}`}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
